import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  Loader2,
  FileText,
  Wand2,
  AlertCircle,
  ChevronRight,
  Copy,
  Check,
} from 'lucide-react';
import {
  generateTemplate,
  TemplateGenerationRequest,
  TemplateGenerationResponse,
} from '../services/claudeAI';

interface AITemplateGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (content: string, variables: any[]) => void;
}

const documentTypes = [
  'Warranty Deed',
  'Quitclaim Deed',
  'LLC Operating Agreement',
  'Articles of Organization',
  'Last Will and Testament',
  'Trust Agreement',
  'Power of Attorney',
  'Lease Agreement',
  'Purchase Agreement',
  'Promissory Note',
  'Bill of Sale',
  'Non-Disclosure Agreement',
  'Employment Agreement',
  'Independent Contractor Agreement',
  'Other',
];

const jurisdictions = [
  'Wisconsin',
  'Florida',
  'Illinois',
  'Minnesota',
  'Michigan',
  'California',
  'New York',
  'Texas',
  'Federal',
  'Other',
];

export function AITemplateGenerator({
  isOpen,
  onClose,
  onGenerated,
}: AITemplateGeneratorProps) {
  const [step, setStep] = useState<'input' | 'generating' | 'result'>('input');
  const [documentType, setDocumentType] = useState('');
  const [customDocType, setCustomDocType] = useState('');
  const [jurisdiction, setJurisdiction] = useState('Wisconsin');
  const [customJurisdiction, setCustomJurisdiction] = useState('');
  const [description, setDescription] = useState('');
  const [result, setResult] = useState<TemplateGenerationResponse | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    const docType = documentType === 'Other' ? customDocType : documentType;
    const jur = jurisdiction === 'Other' ? customJurisdiction : jurisdiction;

    if (!docType || !jur || !description) {
      setError('Please fill in all required fields');
      return;
    }

    setError('');
    setStep('generating');

    try {
      const request: TemplateGenerationRequest = {
        documentType: docType,
        jurisdiction: jur,
        description,
      };

      const response = await generateTemplate(request);
      setResult(response);
      setStep('result');
    } catch (err) {
      console.error('Generation error:', err);
      setError('Failed to generate template. Please check your API key and try again.');
      setStep('input');
    }
  };

  const handleUseTemplate = () => {
    if (result) {
      onGenerated(result.content, result.suggestedVariables);
      handleClose();
    }
  };

  const handleCopy = async () => {
    if (result) {
      await navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setStep('input');
    setDocumentType('');
    setCustomDocType('');
    setDescription('');
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Wand2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Template Generator</h2>
                  <p className="text-sm text-slate-400">
                    Describe your document and let AI create a template
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {step === 'input' && (
                <div className="space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Document Type *
                    </label>
                    <select
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500"
                    >
                      <option value="">Select document type...</option>
                      {documentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {documentType === 'Other' && (
                      <input
                        type="text"
                        value={customDocType}
                        onChange={(e) => setCustomDocType(e.target.value)}
                        placeholder="Enter document type..."
                        className="mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Jurisdiction *
                    </label>
                    <select
                      value={jurisdiction}
                      onChange={(e) => setJurisdiction(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500"
                    >
                      {jurisdictions.map((jur) => (
                        <option key={jur} value={jur}>
                          {jur}
                        </option>
                      ))}
                    </select>
                    {jurisdiction === 'Other' && (
                      <input
                        type="text"
                        value={customJurisdiction}
                        onChange={(e) => setCustomJurisdiction(e.target.value)}
                        placeholder="Enter jurisdiction..."
                        className="mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the document you need. Include any specific requirements, parties involved, special clauses, etc."
                      rows={5}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 resize-none"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Be specific about the purpose, parties, and any special provisions needed.
                    </p>
                  </div>

                  {/* Example prompts */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
                      Example descriptions
                    </p>
                    <div className="space-y-2 text-sm text-slate-400">
                      <p className="cursor-pointer hover:text-slate-300" onClick={() => setDescription('A warranty deed for residential property transfer. The grantor is an individual, and the grantee is married. Include standard covenants and a notary acknowledgment section.')}>
                        • "A warranty deed for residential property transfer..."
                      </p>
                      <p className="cursor-pointer hover:text-slate-300" onClick={() => setDescription('Operating agreement for a two-member LLC. Members have 60/40 ownership split. Manager-managed with unanimous consent required for major decisions. Include distribution, buyout, and dissolution provisions.')}>
                        • "Operating agreement for a two-member LLC..."
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {step === 'generating' && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white animate-pulse" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Generating Your Template
                  </h3>
                  <p className="text-slate-400 mb-6">
                    AI is creating a customized legal document template...
                  </p>
                  <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto" />
                </div>
              )}

              {step === 'result' && result && (
                <div className="space-y-6">
                  {/* Explanation */}
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <h4 className="font-medium text-green-400 mb-2">Template Generated</h4>
                    <p className="text-sm text-green-300/80">{result.explanation}</p>
                  </div>

                  {/* Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">
                        Template Preview
                      </label>
                      <button
                        onClick={handleCopy}
                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto max-h-64">
                      {result.content}
                    </pre>
                  </div>

                  {/* Variables */}
                  {result.suggestedVariables.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Detected Variables ({result.suggestedVariables.length})
                      </label>
                      <div className="space-y-2">
                        {result.suggestedVariables.map((variable, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg"
                          >
                            <FileText className="w-4 h-4 text-violet-400 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white">
                                {variable.name}
                                {variable.required && (
                                  <span className="ml-2 text-xs text-red-400">Required</span>
                                )}
                              </p>
                              <p className="text-xs text-slate-400">
                                {variable.type} • {variable.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-slate-700">
              {step === 'input' && (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg font-medium transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    Generate Template
                  </button>
                </>
              )}

              {step === 'result' && (
                <>
                  <button
                    onClick={() => setStep('input')}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    ← Start Over
                  </button>
                  <button
                    onClick={handleUseTemplate}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-lg font-medium transition-colors"
                  >
                    Use This Template
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AITemplateGenerator;
